import { ICreateDraftVetRequestPayload } from "@entities/aiWorkspace";
import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { FirebaseService } from "@shared/lib/services";
import { Button, Form, Modal, Segmented, Upload } from "@shared/ui";
import { Inbox, Youtube } from "lucide-react";
import { useState } from "react";
import { LinkFields } from "./LinkFields";
import styles from "./styles.module.scss";

type DraftAsset = ICreateDraftVetRequestPayload["mediaAssets"][number];
type Source = "file" | "youtube" | "link";

type UploadItem = {
  name: string;
  type?: string;
  status?: string;
  response?: string;
};

type FormValues = {
  files?: UploadItem[];
  youtubeLinks?: { url: string }[];
  fileLinks?: { url: string }[];
};

const ACCEPT =
  ".mp4,.mov,.jpg,.png,image/jpeg,image/png,video/mp4,video/quicktime,image/gif,image/webp";

function assetType(mime: string, name: string) {
  const value = `${mime} ${name}`.toLowerCase();
  if (/(image|webp|png|jpe?g|gif)/.test(value)) {
    return BriefDeliverableTaskUploadTypeEnum.Image;
  }
  return BriefDeliverableTaskUploadTypeEnum.Video;
}

function nameFromUrl(url: string) {
  try {
    const parsed = new URL(url);
    return parsed.pathname.split("/").filter(Boolean).pop() || parsed.hostname;
  } catch {
    return url;
  }
}

export function DraftUploadModal({
  open,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (assets: DraftAsset[]) => void;
}) {
  const [form] = Form.useForm<FormValues>();
  const [source, setSource] = useState<Source>("file");
  const files = Form.useWatch("files", form);
  const uploading = (files ?? []).some((file) => file.status === "uploading");

  const close = () => {
    form.resetFields();
    setSource("file");
    onClose();
  };

  const submit = (values: FormValues) => {
    if (source === "file") {
      const uploaded = (values.files ?? []).filter((file) => file.response);
      if (!uploaded.length) return;
      onSubmit(
        uploaded.map((file) => ({
          type: assetType(file.type || "", file.name),
          url: file.response || "",
          name: file.name,
        })) as DraftAsset[]
      );
      return;
    }
    const links = source === "youtube" ? values.youtubeLinks : values.fileLinks;
    onSubmit(
      (links ?? []).map((item) => ({
        type:
          source === "youtube"
            ? BriefDeliverableTaskUploadTypeEnum.YoutubeLink
            : BriefDeliverableTaskUploadTypeEnum.MediaLink,
        url: item.url,
        name: nameFromUrl(item.url),
      })) as DraftAsset[]
    );
  };

  return (
    <Modal open={open} title="New draft check" footer={null} onCancel={close}>
      <Segmented
        block
        className={styles.tabs}
        value={source}
        options={[
          { label: "File Upload", value: "file" },
          { label: "YouTube Links", value: "youtube" },
          { label: "File Links", value: "link" },
        ]}
        onChange={(value) => {
          setSource(value as Source);
          form.resetFields();
        }}
      />
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          youtubeLinks: [{ url: "" }],
          fileLinks: [{ url: "" }],
        }}
        onFinish={submit}
      >
        {source === "file" ? (
          <Form.Item
            name="files"
            label="Upload files"
            valuePropName="fileList"
            getValueFromEvent={(event) => event?.fileList}
            rules={[
              { required: true, message: "Please upload at least one file" },
            ]}
          >
            <Upload.Dragger
              multiple
              accept={ACCEPT}
              customRequest={(options) => {
                const file = options.file as File;
                void FirebaseService.uploadFileToFirebase(
                  `draft/${Date.now()}`,
                  file
                )
                  .then((url) => options.onSuccess?.(url))
                  .catch((err: Error) => options.onError?.(err));
              }}
            >
              <Inbox size={28} className={styles.dropIcon} />
              <p className={styles.dropTitle}>Click or drag files here</p>
              <p className={styles.dropHint}>MP4, MOV, JPG, PNG</p>
            </Upload.Dragger>
          </Form.Item>
        ) : null}
        {source === "youtube" ? (
          <LinkFields
            name="youtubeLinks"
            label="YouTube link"
            placeholder="https://youtube.com/watch?v=..."
            addLabel="+ Add YouTube link"
            emptyMessage="Please enter at least one YouTube link."
            prefix={<Youtube size={16} />}
          />
        ) : null}
        {source === "link" ? (
          <LinkFields
            name="fileLinks"
            label="File link"
            placeholder="https://example.com/myfile.mp4"
            addLabel="+ Add file link"
            emptyMessage="Please enter at least one file link."
          />
        ) : null}
        <Button
          className={styles.submitBtn}
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={uploading}
          style={{ marginTop: "16px" }}
        >
          Upload
        </Button>
      </Form>
    </Modal>
  );
}
